import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {RootState, TicketCategory, TicketPriority} from '../types';
import {createTicket} from '../store/slices/ticketsSlice';
import {fetchClients} from '../store/slices/clientsSlice';

interface NewTicketFormProps {
  onSuccess: () => void;
  initialData?: {
    alert_ids?: string[];
    title?: string;
    description?: string;
    client_id?: string;
    client_name?: string;
    priority?: TicketPriority;
    category?: TicketCategory;
    riskScore?: number;
    raw_data?: any | null;
  };
}

interface FormErrors {
  title?: string;
  description?: string;
  client_id?: string;
  raw_data?: string;
}

const NewTicketForm: React.FC<NewTicketFormProps> = ({ onSuccess, initialData }) => {
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state: RootState) => state.tickets);
  const { clients, isLoading: isLoadingClients } = useSelector((state: RootState) => state.clients);

  // Form state
  const [alertIds] = useState<string[]>(initialData?.alert_ids || []);
  const [title, setTitle] = useState(initialData?.title || '');
  const [clientId, setClientID] = useState(initialData?.client_id || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [clientName, setClientName] = useState(initialData?.client_name || '');
  const [priority, setPriority] = useState<TicketPriority>(initialData?.priority || TicketPriority.MEDIUM);
  const [category, setCategory] = useState<TicketCategory>(initialData?.category || TicketCategory.OTHER);
  const [dueDate, setDueDate] = useState('');
  const [rawData, setRawData] = useState(
    initialData?.raw_data ? JSON.stringify(initialData.raw_data, null, 2) : ''
  );

  // Form validation errors
  const [errors, setErrors] = useState<FormErrors>({});

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Parse raw data as JSON if provided
    let parsedRawData = undefined;
    if (rawData.trim()) {
      try {
        parsedRawData = JSON.parse(rawData);
      } catch (e) {
        setErrors({ ...errors, raw_data: 'Invalid JSON format' });
        return;
      }
    }

    const ticketData = {
      alert_ids: alertIds,
      title,
      description,
      client_id: clientId,
      priority,
      category,
      due_date: dueDate || undefined,
      raw_data: parsedRawData,
    };

    dispatch(createTicket(ticketData) as any)
      .unwrap()
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        // Error is handled by the Redux slice
      });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid size = {{xs: 12}}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            disabled={isLoading}
            required
          />
        </Grid>

        {initialData?.client_name && (
          <Grid size={{xs: 12}}>
            <TextField
              fullWidth
              label="Client"
              value={initialData.client_name}
              InputProps={{readOnly: true}}
              disabled
            />
          </Grid>
        )}

        <Grid size = {{xs: 12}}>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={4}
            disabled={isLoading}
            required
          />
        </Grid>

        <Grid size = {{xs: 12}}>

        </Grid>

        <Grid size = {{xs: 12, sm:6}}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              label="Priority"
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              disabled={isLoading}
            >
              {Object.values(TicketPriority).map((p) => (
                <MenuItem key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size = {{xs: 12, sm:6}}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value as TicketCategory)}
              disabled={isLoading}
            >
              {Object.values(TicketCategory).map((c) => (
                <MenuItem key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1).replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size = {{xs: 12}}>
          <TextField
            fullWidth
            label="Due Date"
            type="datetime-local"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            disabled={isLoading}
          />
        </Grid>

        <Grid size = {{xs: 12}}>
          <TextField
              fullWidth
              label="Raw Data (JSON)"
              value={rawData}
              onChange={(e) => setRawData(e.target.value)}
              multiline
              rows={6}
              placeholder='{"key": "value"}'
              disabled={isLoading}
              error={!!errors.raw_data}
              helperText={errors.raw_data}
          />
        </Grid>

        {error && (
          <Grid size = {{xs: 12}}>
            <FormHelperText error>{error}</FormHelperText>
          </Grid>
        )}

        <Grid size = {{xs: 12}}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Create Ticket'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewTicketForm;
